// src/components/dashboard/DashboardFilters.jsx
import React, { useState } from 'react';
import Select from "react-select";
import { optionsPartido, optionsEstado, optionsAreaResponsable } from '../../datos/options';
import optionsTipos from '../../datos/options.json';
import InfoButtonWithPopover from "../InfoButtonWithPopover";

const DashboardFilters = ({
	filters,
	onFilterChange,
	onReset,
	loading = false,
	isRefetching = false,
	customFilters = [],
	showSearch = true,
	showDateRange = false,
	tiposPorArea = null
}) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const years = ['2016', '2017', '2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025', '2026'];
	
	// Catálogo completo de tipos/subtipos (options.json) filtrado por los tipos reales del área seleccionada
	const tiposCatalogo = tiposPorArea
		? optionsTipos.tipos.filter(t => tiposPorArea.includes(t.nombre))
		: optionsTipos.tipos;
	const tipos = ['Todos', ...tiposCatalogo.map(t => t.nombre)];
	const subtiposDelTipo = tiposCatalogo.find(t => t.nombre === filters.tipo)?.subtipos || [];
	// Estados reales presentes en la BD + los del catálogo del formulario (sin duplicados)
	const estadosUnicos = [
		...optionsEstado.map(e => e.value),
		'Adenda', 'Desestimada', 'En Ejecución'
	].filter((v, i, arr) => arr.indexOf(v) === i);
	const estados = ['Todos', ...estadosUnicos];

	// Secciones electorales únicas para el filtro (todavía sin implementar)
	const seccionesElectorales = ['Todos', ...new Set(optionsPartido.map(p => p.seccion_electoral))];

	// 💥 Gatilla el cambio inmediatamente hacia el hook padre
	const handleChange = (name, value) => {
		if (onFilterChange) {
			onFilterChange({ target: { name, value } });
			// Si cambia el tipo, reseteamos el subtipo para que no quede uno de otro tipo
			if (name === 'tipo') {
				onFilterChange({ target: { name: 'subtipo', value: 'Todos' } });
			}
			// Si cambia el área responsable, reseteamos tipo y subtipo
			if (name === 'area_responsable') {
				onFilterChange({ target: { name: 'tipo', value: 'Todos' } });
				onFilterChange({ target: { name: 'subtipo', value: 'Todos' } });
			}
		}
	};

	const handleKeyPress = (e) => {
		if (e.key === 'Enter' && showSearch) {
			// La búsqueda puede requerir un comportamiento especial o directo si lo deseás
			handleChange('search', e.target.value);
		}
	};

	return (
		<div className="mb-6 rounded-xl border border-gray-200 bg-white shadow-sm transition-all">
			{/* Header del filtro */}
			<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
				<div className="flex items-center gap-2">
					<svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
					</svg>
					<h3 className="font-semibold text-gray-700">Filtros Activos</h3>
					<span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
						{Object.keys(filters).filter(k => filters[k] && filters[k] !== 'Todas' && filters[k] !== 'Todos' && filters[k] !== '').length}
					</span>
				</div>

				<button
					onClick={() => setIsExpanded(!isExpanded)}
					className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
				>
					{isExpanded ? 'Simplificar' : 'Más filtros'}
					<svg className={`h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
					</svg>
				</button>
			</div>

			{/* Contenido de filtros */}
			<div className="p-4">
				{/* Fila 1: Año / Partidos / Estado / Sección Electoral */}
				<div className="flex flex-wrap gap-4">
					{/* Año */}
					<div className="flex-1 min-w-[120px]">
						<div className="flex items-center">
							<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Año</label>
							<InfoButtonWithPopover infoText="Año del expediente del proyecto (EX-AAAA), no la fecha de carga" />
						</div>
						<select
							name="year"
							value={filters.year || 'Todos'}
							onChange={(e) => handleChange('year', e.target.value)}
							disabled={loading}
							className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
						>
							<option value="Todos">Todos los Años</option>
							{years.map(y => (
								<option key={y} value={y}>{y}</option>
							))}
						</select>
					</div>

					{/* Selector de Partido */}
					<div className="flex-1 min-w-[250px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">
							Partidos / Municipios (Selección Múltiple)
						</label>
						<div className="relative">
							<Select
								id="partido"
								isMulti
								options={[
									{ value: 'Todos', label: '⚠️ Todos los Partidos' },
									...optionsPartido.map(p => ({
										value: p.value,
										label: `${p.label} (${p.seccion_electoral})`
									}))
								]}
								value={
									(() => {
										const currentPartido = filters.partido || 'Todos';
										if (currentPartido === 'Todos' || currentPartido === 'Todas') {
											return [{ value: 'Todos', label: '⚠️ Todos los Partidos' }];
										}
										const partidosArray = Array.isArray(currentPartido)
											? currentPartido
											: currentPartido.split(',');
										return optionsPartido
											.filter(p => partidosArray.includes(String(p.value)))
											.map(p => ({
												value: p.value,
												label: `${p.label} (${p.seccion_electoral})`
											}));
									})()
								}
								onChange={(selectedOptions) => {
									if (!selectedOptions || selectedOptions.length === 0) {
										if (onFilterChange) {
											onFilterChange({ target: { name: 'partido', value: 'Todos' } });
										}
										return;
									}
									const valores = selectedOptions.map(o => o.value);
									let resultado = valores;
									const teniaTodos = (filters.partido === 'Todos' || filters.partido === 'Todas' || !filters.partido);
									if (valores.includes('Todos') && valores.length > 1) {
										if (teniaTodos) {
											resultado = valores.filter(v => v !== 'Todos');
										} else {
											resultado = ['Todos'];
										}
									}
									if (onFilterChange) {
										onFilterChange({
											target: {
												name: 'partido',
												value: resultado
											}
										});
									}
								}}
								placeholder="Seleccionar partidos..."
								className="mt-1"
							/>
							<p className="text-[10px] text-gray-400 mt-1">Mantenga presionado Ctrl (Cmd en Mac) para elegir varios.</p>
						</div>
					</div>

					{/* Estado */}
					<div className="flex-1 min-w-[180px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">
							Estado (Selección Múltiple)
						</label>
						<div className="relative">
							<Select
								id="estado"
								isMulti
								options={[
									{ value: 'Todos', label: '⚠️ Todos los Estados' },
									...estadosUnicos.map(e => ({ value: e, label: e }))
								]}
								value={
									(() => {
										const currentEstado = filters.estado || 'Todos';
										if (currentEstado === 'Todos' || currentEstado === 'Todas') {
											return [{ value: 'Todos', label: '⚠️ Todos los Estados' }];
										}
										const estadosArray = Array.isArray(currentEstado)
											? currentEstado
											: currentEstado.split(',');
										return estadosUnicos
											.filter(e => estadosArray.includes(e))
											.map(e => ({ value: e, label: e }));
									})()
								}
								onChange={(selectedOptions) => {
									if (!selectedOptions || selectedOptions.length === 0) {
										if (onFilterChange) {
											onFilterChange({ target: { name: 'estado', value: 'Todos' } });
										}
										return;
									}
									const valores = selectedOptions.map(o => o.value);
									let resultado = valores;
									const teniaTodos = (filters.estado === 'Todos' || filters.estado === 'Todas' || !filters.estado);
									if (valores.includes('Todos') && valores.length > 1) {
										if (teniaTodos) {
											resultado = valores.filter(v => v !== 'Todos');
										} else {
											resultado = ['Todos'];
										}
									}
									if (onFilterChange) {
										onFilterChange({ target: { name: 'estado', value: resultado } });
									}
								}}
								placeholder="Seleccionar estados..."
								className="mt-1"
							/>
							<p className="text-[10px] text-gray-400 mt-1">Mantenga presionado Ctrl (Cmd en Mac) para elegir varios.</p>
						</div>
					</div>

					{/* Sección Electoral (pendiente de implementar) */}
					<div className="flex-1 min-w-[180px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Sección Electoral</label>
						<select
							name="seccion_electoral"
							value={filters.seccion_electoral || 'Todos'}
							onChange={(e) => handleChange('seccion_electoral', e.target.value)}
							disabled={true}
							className="w-full cursor-not-allowed rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-400"
						>
							{seccionesElectorales.map(s => (
								<option key={s} value={s}>{s}</option>
							))}
						</select>
					</div>
				</div>

				{/* Fila 2 (más filtros): Área Responsable / Tipo / Subtipo / vacío */}
				{isExpanded && (
					<div className="mt-4 border-t border-gray-200 pt-4 animate-fadeIn">
						<div className="flex flex-wrap gap-4">
							{/* Área Responsable */}
							<div className="flex-1 min-w-[220px]">
								<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Área Responsable</label>
								<select
									name="area_responsable"
									value={filters.area_responsable || 'Todos'}
									onChange={(e) => handleChange('area_responsable', e.target.value)}
									disabled={loading}
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
								>
									<option value="Todos">Todas las áreas</option>
									{optionsAreaResponsable.map(a => (
										<option key={a.value} value={a.value}>{a.label}</option>
									))}
								</select>
							</div>

							{/* Tipo de obra (depende del área responsable) */}
							<div className="flex-1 min-w-[200px]">
								<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Tipo de obra</label>
								<select
									name="tipo"
									value={filters.tipo || 'Todos'}
									onChange={(e) => handleChange('tipo', e.target.value)}
									disabled={loading}
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
								>
									{tipos.map(t => (
										<option key={t} value={t}>{t}</option>
									))}
								</select>
							</div>

							{/* Subtipo de obra (dependiente del tipo) */}
							<div className="flex-1 min-w-[220px]">
								<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Subtipo de obra</label>
								<select
									name="subtipo"
									value={filters.subtipo || 'Todos'}
									onChange={(e) => handleChange('subtipo', e.target.value)}
									disabled={loading || !filters.tipo || filters.tipo === 'Todos'}
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
								>
									<option value="Todos">Todos los subtipos</option>
									{subtiposDelTipo.map(s => (
										<option key={s.nombre} value={s.nombre}>{s.nombre}</option>
									))}
								</select>
							</div>

							{/* Espacio vacío para completar la grilla */}
							<div className="flex-1 min-w-[120px]" aria-hidden="true"></div>
						</div>
					</div>
				)}

				{/* Botón único de Resetear */}
				<div className="mt-4 flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
					{(loading || isRefetching) && (
						<span className="flex items-center gap-2 text-xs text-gray-400 animate-pulse">
							<svg className="h-4 w-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
								<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
								<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
							</svg>
							Sincronizando datos...
						</span>
					)}
					<button
						onClick={onReset}
						disabled={loading}
						className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100 disabled:opacity-50"
					>
						<svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
						</svg>
						Resetear filtros
					</button>
				</div>
			</div>
		</div>
	);
};

export default DashboardFilters;